import { useForm } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import rule from '@/libs/antd/rules';
import {
	getNAPInternalComplianceDetail,
	NAPInternalCompliancePayload,
	NAPState,
	submitNAPInternalCompliance,
	verifyNAPInternalCompliance,
} from '@/libs/fleet-api/services/nap';
import {
	LoadingOutlined,
	UploadOutlined,
	InfoCircleFilled,
} from '@ant-design/icons';
import {
	Alert,
	Button,
	Col,
	Divider,
	Form,
	Row,
	Select,
	Typography,
	Upload,
	UploadFile,
} from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { FC, Fragment } from 'react';
import NAPAnalisaSLIKPage from './NAPPage.Fullfillment.slik';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FileData } from '@/libs/fleet-api/services/types';
import { uploader } from '@/libs/fleet-api/uploader';
import { useUserData } from '@/libs/fleet-api/hooks';
import ComplianceInfoModal, {
	useComplianceInfoModal,
} from './modals/ComplianceInfoModal';

const NAPFullfillment: FC<{
	napState?: NAPState;
	prospectId: string;
	customerId: string;
	onSubmitted: () => void;
}> = ({ prospectId, customerId, napState, onSubmitted }) => {
	const getNAPInternalComplianceQuery = useQuery({
		queryKey: ['submitNAPInternalCompliance', prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return null;
			return getNAPInternalComplianceDetail(id);
		},
	});

	const complianceInfoModal = useComplianceInfoModal();

	const { data } = getNAPInternalComplianceQuery.data ?? {};
	const { groupName } = useUserData() ?? {};
	const { form, fieldName } = useForm<NAPInternalCompliancePayload>();

	const uploadMutation = useMutation({
		mutationFn: uploader,
	});

	const submitNAPInternalComplianceMutation = useMutation({
		mutationFn: submitNAPInternalCompliance,
	});

	const verifyNAPInternalComplianceMutation = useMutation({
		mutationFn: verifyNAPInternalCompliance,
	});

	const isReadOnly =
		napState?.action === 'VIEW' || napState?.action === 'DISABLED';

	if (getNAPInternalComplianceQuery.isFetching) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	return (
		<Fragment>
			<ComplianceInfoModal {...complianceInfoModal} />
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				labelCol={{
					style: {
						width: 250,
					},
				}}
				onFinish={async (values) => {
					if (groupName === 'FRM') {
						await submitNAPInternalComplianceMutation.mutateAsync(values);
					}

					if (groupName === 'CCA') {
						await verifyNAPInternalComplianceMutation.mutateAsync(values);
					}

					onSubmitted();
				}}
			>
				<Form.Item
					name={fieldName('napId')}
					initialValue={data?.napId}
					noStyle
				/>
				<Form.Item
					name={fieldName('internalComplianceId')}
					initialValue={data?.internalComplianceId}
					noStyle
				/>
				<Form.Item
					label="Portofolio Guideline"
					initialValue={data?.portofolioGuideline}
				>
					<div className="ant-form-item-control-input-content">
						<div
							className="ant-input ant-input-outlined ant-input-disabled"
							style={{ paddingBottom: 80 }}
						>
							<div
								dangerouslySetInnerHTML={{
									__html: data?.portofolioGuideline || '',
								}}
							/>
						</div>
					</div>
				</Form.Item>

				<Divider />

				<Form.Item
					label="Informasi Tambahan Portofolio Guideline"
					name={fieldName('additionalPortofolioGuideline')}
					rules={[rule.required()]}
					initialValue={data?.notes}
				>
					<TextArea
						rows={3}
						disabled={isReadOnly}
					/>
				</Form.Item>

				<Alert
					message="Informasi tambahan dapat diisi dengan Kode Sektor, Kelonggaran Limit Sektor, Tanggal Pengecekan dll."
					type="info"
					showIcon
					style={{ marginTop: 30 }}
				/>

				<Divider />

				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item
							name={fieldName('customerRatingId')}
							label={
								<>
									Rating Customer
									<InfoCircleFilled
										style={{ color: '#1677ff', marginLeft: 2, marginRight: 2 }}
										onClick={() => {
											complianceInfoModal.open({
												title: 'Contoh Pengisian Rating Customer',
												info: 'Berdasarkan hasil pengecekan customer rating di portalrisk.mtf.co.id pada XX __ oleh Fleet Relationship Manager , PT X mendapatkan rating AAA (berdasarkan laporan keuangan tahun __).',
											});
										}}
									/>
								</>
							}
							initialValue={data?.customerRating?.id}
							rules={[rule.required()]}
						>
							<Select
								options={data?.customerRatingOptions?.map((data) => {
									return {
										value: data.id,
										label: data.value,
									};
								})}
								disabled={isReadOnly}
							/>
						</Form.Item>

						<div style={{ height: 20 }} />

						<Form.Item
							name={fieldName('customerRatingEvidenceDocumentId')}
							initialValue={data?.customerRatingEvidenceDocument}
							label="Evidence"
							rules={[rule.required()]}
							getValueProps={(value) => {
								const file = value as FileData;
								return {
									fileList: file?.id
										? ([
												{
													uid: file.id,
													name: file.name,
													thumbUrl: file.url,
												},
											] as UploadFile[])
										: [],
								};
							}}
							normalize={({ file }: { file: UploadFile }) => {
								return {
									id: file.uid,
									format: file.type,
									name: file.name,
									url: file.thumbUrl,
								} as FileData;
							}}
						>
							<Upload.Dragger
								listType="picture"
								accept=".jpg,.jpeg,.png"
								maxCount={1}
								disabled={isReadOnly}
								beforeUpload={() => false}
								onRemove={() => {
									form.setFieldValue(
										fieldName('customerRatingEvidenceDocumentId'),
										null,
									);
									return false;
								}}
								onChange={async ({ file }) => {
									const fileid = Date.now().toString();
									const filepath = `customer/${customerId}/${prospectId}/temp`;

									try {
										const url = await uploadMutation.mutateAsync({
											file: file as unknown as File,
											fileid,
											filepath,
										});

										form.setFieldValue(
											fieldName('customerRatingEvidenceDocumentId'),
											{
												format: 'jpeg',
												id: fileid,
												name: file.name,
												path: filepath,
												url,
											},
										);
									} catch {
										form.setFieldValue(
											fieldName('customerRatingEvidenceDocumentId'),
											null,
										);
									}
								}}
							>
								<UploadOutlined
									style={{
										fontSize: 25,
										color: '#BABABA',
										display: 'block',
									}}
								/>
							</Upload.Dragger>
						</Form.Item>
					</Col>
				</Row>

				<div style={{ height: 20 }} />

				<Form.Item
					name={fieldName('customerratingInformation')}
					label="Informasi Rating Customer"
					initialValue={data?.customerRatingInformation}
					rules={[rule.required()]}
				>
					<TextArea
						rows={3}
						disabled={isReadOnly}
					/>
				</Form.Item>
				<Divider />

				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item
							name={fieldName('bouwheerRatingId')}
							label="Rating Bouwheer/Buyer"
							rules={groupName === 'CCA' ? [rule.required()] : []}
							initialValue={data?.bouwheerRating?.id}
						>
							<Select
								options={data?.bouwheerRatingOptions?.map((data) => {
									return {
										value: data.id,
										label: data.value,
									};
								})}
								disabled={isReadOnly || groupName === 'FRM'}
							/>
						</Form.Item>

						<div style={{ height: 20 }} />

						<Form.Item
							name={fieldName('bouwheerRatingEvidenceDocumentId')}
							initialValue={data?.bouwheerRatingEvidenceDocument}
							label="Evidence"
							rules={groupName === 'CCA' ? [rule.required()] : []}
							getValueProps={(value) => {
								const file = value as FileData;
								return {
									fileList: file?.id
										? ([
												{
													uid: file.id,
													name: file.name,
													thumbUrl: file.url,
												},
											] as UploadFile[])
										: [],
								};
							}}
							normalize={({ file }: { file: UploadFile }) => {
								return {
									id: file.uid,
									format: file.type,
									name: file.name,
									url: file.thumbUrl,
								} as FileData;
							}}
						>
							<Upload.Dragger
								listType="picture"
								accept=".jpg,.jpeg,.png"
								maxCount={1}
								beforeUpload={() => false}
								disabled={isReadOnly || groupName === 'FRM'}
								onRemove={() => {
									form.setFieldValue(
										fieldName('bouwheerRatingEvidenceDocumentId'),
										null,
									);
									return false;
								}}
								onChange={async ({ file }) => {
									const fileid = Date.now().toString();
									const filepath = `customer/${customerId}/${prospectId}/site-observation-photos`;

									try {
										const url = await uploadMutation.mutateAsync({
											file: file as unknown as File,
											fileid,
											filepath,
										});
										form.setFieldValue(
											fieldName('bouwheerRatingEvidenceDocumentId'),
											{
												format: 'jpeg',
												id: fileid,
												name: file.name,
												path: filepath,
												url,
											},
										);
									} catch {
										form.setFieldValue(
											fieldName('bouwheerRatingEvidenceDocumentId'),
											null,
										);
									}
								}}
							>
								<UploadOutlined
									style={{
										fontSize: 25,
										color: '#BABABA',
										display: 'block',
									}}
								/>
							</Upload.Dragger>
						</Form.Item>
					</Col>
				</Row>

				<div style={{ height: 20 }} />

				<Form.Item
					name={fieldName('bouwheerRatingInformation')}
					initialValue={data?.bouwheerRatingInformation}
					label="Informasi Rating Bouwheer"
					rules={groupName === 'CCA' ? [rule.required()] : []}
				>
					<TextArea
						rows={3}
						disabled={isReadOnly || groupName === 'FRM'}
					/>
				</Form.Item>

				<Divider />

				<Typography.Title level={3}>Slik OJK dan Pengurus</Typography.Title>

				<NAPAnalisaSLIKPage prospectId={prospectId} />

				<Divider />

				<Form.Item
					name={fieldName('eddHighRiskAndPepAnalysis')}
					initialValue={data?.eddHighRiskAndPepAnalysis}
					label={
						<>
							Analisa EDD (Enhanced Due Diligence) High Risk & Politically
							Exposed Person (PEP)
							<InfoCircleFilled
								style={{ color: '#1677ff', marginLeft: 2, marginRight: 2 }}
								onClick={() => {
									complianceInfoModal.open({
										title: 'Contoh Pengisian Analisa EDD & PEP',
										info: 'Proses verifikasi terhadap customer, shareholder, dan beneficial owner yang dianggap dan/atau diklasifisikasikan memiliki resiko tinggi terhadap praktik TPPU/PT (Tindak Pidana Pencucian Uang/Pendanaan Terorisme) dan memberikan mitigasi atas resiko-resiko yang muncul.',
									});
								}}
							/>
						</>
					}
				>
					<TextArea
						rows={3}
						disabled={isReadOnly}
					/>
				</Form.Item>

				<Divider />

				<Row gutter={[40, 20]}>
					<Col
						span={5}
						style={{ maxWidth: 250 }}
					>
						<Form.Item
							label={
								<>
									CPA/PAT
									<InfoCircleFilled
										style={{ color: '#1677ff', marginLeft: 2, marginRight: 2 }}
										onClick={() => {
											complianceInfoModal.open({
												title: 'Contoh Pengisian CPA/PAT',
												info: 'Sesuai dengan hasil perhitungan CPA, debitur akan menyumbang pendapatan kepada Bank Mandiri sebesar Rp 5.106 Juta atau sekitar 2,82% dari total fasilitas Kredit Modal Kerja yang diberikan. Oleh karena itu, akan dilakukan beberapa strategi di antaranya sbb:<br/>a. Mengarahkan debitur menggunakan asuransi MTF Clause.<br />b. dsb',
											});
										}}
									/>
									<span style={{ color: 'rgb(225, 1, 1)' }}>*</span>
								</>
							}
						/>
					</Col>
					<Col
						span={7}
						style={{ paddingLeft: 0 }}
					>
						<Form.Item
							name={fieldName('cpaPatDocumentId')}
							initialValue={data?.cpaPatDocument}
							label=""
							style={{ marginLeft: 22 }}
							rules={[rule.required()]}
							getValueProps={(value) => {
								const file = value as FileData;
								return {
									fileList: file?.id
										? ([
												{
													uid: file.id,
													name: file.name,
													thumbUrl: file.url,
												},
											] as UploadFile[])
										: [],
								};
							}}
							normalize={({ file }: { file: UploadFile }) => {
								return {
									id: file.uid,
									format: file.type,
									name: file.name,
									url: file.thumbUrl,
								} as FileData;
							}}
						>
							<Upload.Dragger
								listType="picture"
								accept=".jpg,.jpeg,.png"
								maxCount={1}
								disabled={isReadOnly}
								beforeUpload={() => false}
								onRemove={() => {
									form.setFieldValue(fieldName('cpaPatDocumentId'), null);
									return false;
								}}
								onChange={async ({ file }) => {
									const fileid = Date.now().toString();
									const filepath = `customer/${customerId}/${prospectId}/site-observation-photos`;

									try {
										const url = await uploadMutation.mutateAsync({
											file: file as unknown as File,
											fileid,
											filepath,
										});
										form.setFieldValue(fieldName('cpaPatDocumentId'), {
											format: 'jpeg',
											id: fileid,
											name: file.name,
											path: filepath,
											url,
										});
									} catch {
										form.setFieldValue(fieldName('cpaPatDocumentId'), null);
									}
								}}
							>
								<UploadOutlined
									style={{
										fontSize: 25,
										color: '#BABABA',
										display: 'block',
									}}
								/>
							</Upload.Dragger>
						</Form.Item>
					</Col>
					<Col span={12} />
				</Row>

				<Divider />

				<Form.Item
					name={fieldName('cpaPatInformation')}
					initialValue={data?.cpaPatInformation}
					label="Informasi CPA/PAT"
					rules={[rule.required()]}
				>
					<TextArea
						rows={3}
						disabled={isReadOnly}
					/>
				</Form.Item>

				<Divider />

				<Form.Item
					label={groupName === 'FRM' ? 'Informasi Tambahan' : 'Notes FRM'}
					name={fieldName('notes')}
					initialValue={data?.notes}
					rules={groupName === 'FRM' ? [rule.required()] : []}
				>
					<TextArea
						rows={3}
						disabled={isReadOnly || groupName !== 'FRM'}
					/>
				</Form.Item>
				<Divider />
				{groupName !== 'FRM' && (
					<Form.Item
						label={groupName === 'CCA' ? 'Verification Notes' : 'Notes CCA'}
						style={{ marginTop: 20 }}
						name={'verificationNotes'}
						initialValue={data?.verificationNotes}
						rules={groupName === 'CCA' ? [rule.required()] : []}
					>
						<TextArea
							rows={3}
							disabled={isReadOnly || groupName !== 'CCA'}
						/>
					</Form.Item>
				)}

				<Button
					type="primary"
					htmlType="submit"
					style={{
						width: 200,
						display: 'block',
						marginLeft: 'auto',
						marginTop: 40,
					}}
					disabled={
						submitNAPInternalComplianceMutation.isPending ||
						verifyNAPInternalComplianceMutation.isPending ||
						isReadOnly
					}
				>
					NEXT
				</Button>
			</Form>
		</Fragment>
	);
};

export default NAPFullfillment;
